import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import EditIcon from '@mui/icons-material/Edit'
import FilterListIcon from '@mui/icons-material/FilterList'
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tab,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useContext, useEffect, useMemo, useState } from 'react'
import {
  ConfirmOUStatusData,
  freezeViewEditAdoptionStatus,
  getConfirmOUStatus,
  getViewEditAdoptionStatusById,
  getViewEditAdoptionStatusFilters,
  getViewEditAdoptionStatusList,
  postConfirmOUStatus,
  updateViewEditAdoptionStatus,
  UserRole,
  ViewEditAdoptionStatusRow,
} from '../../api'
import EmptyBox from '../../assets/empty_box.svg'
import FetchError from '../../assets/fetch_error.svg'
import { AppStateContext } from '../../state/AppProvider'
import styles from './AdoptionDashboard.module.css'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CURRENT_YEAR = new Date().getFullYear()
const MIN_YEAR = 2024
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - MIN_YEAR + 2 }, (_, i) => MIN_YEAR + i)
const QUARTER_OPTIONS = ['YTD', 'Q1', 'Q2', 'Q3', 'Q4']

const BPD_ACTIVATION_STATUS_OPTIONS = [
  'Activated',
  'Plan to Activate',
  'Do Not Plan to Activate',
  'Paused',
  'Ineligible',
  'Maybe',
]

const MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const OU_LIST = ['Africa', 'ASP', 'EME', 'Europe', 'GCM', 'INSWA']

const BRAND_OPTIONS = [
  'AdeS',
  'AHA',
  'Aquarius',
  'ATH',
  'Benedictino',
  'Chunye',
  'Ciel',
  'Coca-Cola TM',
  'Coca-Cola Zero Sugar',
  'Crystal',
  'Del Valle',
  'Diet Coke/Coca-Cola Light',
  'Fanta',
  'Frestea',
  'Fuze Tea',
  'GEORGIA',
  'Gold Peak',
  'Limca Sportz',
  'Mezzo Mix',
  'Minute Maid',
  'Minute Maid Zero Sugar',
  'Mount Franklin',
  'Multiple',
  'Nutriboost',
  'OOHA',
  'Powerade',
  'Real Leaf',
  'Santa Clara',
  'Schweppes',
  'Smartwater',
  'Sprite',
  'Thumbs Up',
  'Topo Chico',
  'Vitamin Water',
  'Wilkins',
  'Yangguang Tea',
  'Other',
  'Not Applicable',
]

const CHARTER_OPTIONS = [
  'Corporate: Corporate',
  'Global CCTM: Christmas',
  'Global CCTM: Meals',
  'Global CCTM: Nucleous',
  'Global CCTM: Reborn',
  'Global CCTM: Share a Coke',
  'Global CCTM: Uplift',
  'Global CCTM: Zero Sugar',
  'Global Flavors: Fanta/Halloween',
  'Global Flavors: Fanta/Snacking',
  'Global Flavors: Fanta/Wanta',
  'Global Flavors: Sprite/Daily Commute',
  'Global Flavors: Sprite/Spicy',
  'Global Flavors: Sprite/Summer',
  'Not under the list above',
]

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ViewEditSortColumn =
  | 'ou'
  | 'market'
  | 'mto_initiative'
  | 'bpd_name'
  | 'bpd_launched_in'
  | 'status'
  | 'indicate_tentative_month'
  | 'eligible_mkt'

type SortDirection = 'asc' | 'desc'

type FilterState = {
  ou?: string
  mto_initiative?: string
  bpd?: string
  status?: string
}

type EditPayload = {
  status?: string
  indicate_tentative_month?: string
  brands?: string[]
  charters?: string[]
  blocker_comment?: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ACTIVATED_MIN_YEAR = 2023
const PLAN_TO_ACTIVATE_PREFIX = 'Plan to Activate in'
const MAYBE_PREFIX = 'Maybe for'

const getEditStatusOptions = (selectedYear: number): string[] => {
  // +2: +1 for inclusive range, +1 to include next year (e.g. if current year is 2026, max year will be 2027)
  const activatedOptions = Array.from(
    { length: CURRENT_YEAR - ACTIVATED_MIN_YEAR + 2 },
    (_, i) => `Activated in ${ACTIVATED_MIN_YEAR + i}`,
  )
  return [
    ...activatedOptions,
    `${PLAN_TO_ACTIVATE_PREFIX} ${selectedYear}`,
    `${PLAN_TO_ACTIVATE_PREFIX} ${selectedYear + 1}`,
    `${MAYBE_PREFIX} ${selectedYear}`,
    `${MAYBE_PREFIX} ${selectedYear + 1}`,
    'Do Not Plan to Activate',
    'Paused',
    'Ineligible',
  ]
}

const getRowClass = (status: string | undefined): string => {
  if (!status) return ''
  const s = status.toLowerCase()
  if (s.includes('activated')) return styles.rowActivated
  if (s.includes('ineligible') || s.includes('paused')) return styles.rowNeutral
  return styles.rowPlanned
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ViewEditAdoptionStatusTab = () => {
  const appStateContext = useContext(AppStateContext)

  // Period
  const [selectedYear, setSelectedYear] = useState<number>(CURRENT_YEAR)
  const [selectedQuarter, setSelectedQuarter] = useState<string>('YTD')

  // Filter options
  const [mtoInitiativeOptions, setMtoInitiativeOptions] = useState<string[]>([])
  const [ouOptions, setOuOptions] = useState<string[]>([])
  const [bpdOptions, setBpdOptions] = useState<string[]>([])

  // List data
  const [items, setItems] = useState<ViewEditAdoptionStatusRow[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [fetchError, setFetchError] = useState<boolean>(false)

  // Sort
  const [sortColumn, setSortColumn] = useState<ViewEditSortColumn>('ou')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Filter toolbar toggle
  const [nonActivatedMarketsToggle, setNonActivatedMarketsToggle] = useState<boolean>(true)

  // Filters modal
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false)
  const [pendingFilters, setPendingFilters] = useState<FilterState>({})
  const [appliedFilters, setAppliedFilters] = useState<FilterState>({})

  // Edit modal
  const [editOpen, setEditOpen] = useState<boolean>(false)
  const [editRecord, setEditRecord] = useState<ViewEditAdoptionStatusRow | null>(null)
  const [editLoading, setEditLoading] = useState<boolean>(false)
  const [editPayload, setEditPayload] = useState<EditPayload>({})

  // Freeze Data
  const [freezing, setFreezing] = useState<boolean>(false)
  const [freezeConfirmOpen, setFreezeConfirmOpen] = useState<boolean>(false)

  // Confirm OU Status modal
  const [confirmOUOpen, setConfirmOUOpen] = useState<boolean>(false)
  const [confirmOUPeriod, setConfirmOUPeriod] = useState<string>(`Q4 ${CURRENT_YEAR}`)
  const [confirmOUData, setConfirmOUData] = useState<ConfirmOUStatusData>({ sustainability: {}, other_learnings: {} })
  const [confirmOUTab, setConfirmOUTab] = useState<number>(0)
  const [confirmOULoading, setConfirmOULoading] = useState<boolean>(false)

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchList = async (year: number, quarter: string, filters: FilterState, nonActivated?: boolean) => {
    setLoading(true)
    setFetchError(false)
    try {
      const response = await getViewEditAdoptionStatusList(year, quarter, {
        ...filters,
        non_activated_markets: nonActivated || undefined,
      })
      if (response) {
        setItems(response.items)
      } else {
        setFetchError(true)
        setItems([])
      }
    } catch {
      setFetchError(true)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getViewEditAdoptionStatusFilters(selectedYear)
      .then(options => {
        setMtoInitiativeOptions(options?.mto_initiatives ?? [])
        setOuOptions(options?.ous ?? [])
        setBpdOptions(options?.bpds ?? [])
      })
      .catch(() => {})
  }, [selectedYear])

  useEffect(() => {
    fetchList(selectedYear, selectedQuarter, appliedFilters, nonActivatedMarketsToggle || undefined)
  }, [selectedYear, selectedQuarter, appliedFilters, nonActivatedMarketsToggle])

  // ---------------------------------------------------------------------------
  // Handlers — period
  // ---------------------------------------------------------------------------

  const handleYearChange = (year: number) => {
    setSelectedYear(year)
  }

  const handleQuarterChange = (quarter: string) => {
    setSelectedQuarter(quarter)
  }

  const handleResetFilters = () => {
    setSelectedYear(CURRENT_YEAR)
    setAppliedFilters({})
    setPendingFilters({})
    setNonActivatedMarketsToggle(true)
  }

  // ---------------------------------------------------------------------------
  // Handlers — freeze data
  // ---------------------------------------------------------------------------

  const handleFreezeData = async () => {
    setFreezeConfirmOpen(false)
    setFreezing(true)
    try {
      const result = await freezeViewEditAdoptionStatus(selectedYear)
      if (result) {
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: {
            message: `Successfully froze ${result.count} YTD entries for ${selectedYear}.`,
            severity: 'success',
          },
        })
        fetchList(selectedYear, selectedQuarter, appliedFilters, nonActivatedMarketsToggle || undefined)
      } else {
        appStateContext?.dispatch({
          type: 'SET_SNACKBAR_STATE',
          payload: { message: 'Failed to freeze data. Please try again.', severity: 'warning' },
        })
      }
    } catch {
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'An unexpected error occurred while freezing data.', severity: 'warning' },
      })
    } finally {
      setFreezing(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Handlers — filters modal
  // ---------------------------------------------------------------------------

  const handleOpenFilters = () => {
    setPendingFilters({ ...appliedFilters })
    setFiltersOpen(true)
  }

  const handleApplyFilters = () => {
    setAppliedFilters({ ...pendingFilters })
    setFiltersOpen(false)
  }

  const handleClearFilters = () => {
    setPendingFilters({})
  }

  // ---------------------------------------------------------------------------
  // Handlers — edit modal
  // ---------------------------------------------------------------------------

  const handleOpenEdit = async (row: ViewEditAdoptionStatusRow) => {
    setEditLoading(true)
    setEditOpen(true)
    setEditPayload({
      status: row.status ?? '',
      indicate_tentative_month: row.indicate_tentative_month ?? '',
      brands: row.brands ?? [],
      charters: row.charters ?? [],
      blocker_comment: row.blocker_comment ?? '',
    })
    try {
      const full = await getViewEditAdoptionStatusById(row.id)
      if (full) {
        setEditRecord(full)
        setEditPayload({
          status: full.status ?? '',
          indicate_tentative_month: full.indicate_tentative_month ?? '',
          brands: full.brands ?? [],
          charters: full.charters ?? [],
          blocker_comment: full.blocker_comment ?? '',
        })
      } else {
        setEditRecord(row)
      }
    } catch {
      setEditRecord(row)
    } finally {
      setEditLoading(false)
    }
  }

  const handleCloseEdit = () => {
    setEditOpen(false)
    setEditRecord(null)
    setEditPayload({})
  }

  const handleSubmitEdit = async () => {
    if (!editRecord) return
    try {
      await updateViewEditAdoptionStatus(editRecord.id, editPayload)
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Adoption Status updated successfully.', severity: 'success' },
      })
      handleCloseEdit()
      fetchList(selectedYear, selectedQuarter, appliedFilters, nonActivatedMarketsToggle || undefined)
    } catch {
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Failed on updating Adoption Status. Try again later.', severity: 'warning' },
      })
    }
  }

  // ---------------------------------------------------------------------------
  // Handlers — Confirm OU Status
  // ---------------------------------------------------------------------------

  const emptyOUChecklist = () => Object.fromEntries(OU_LIST.map(ou => [ou, false]))

  const loadConfirmOUData = async (period: string) => {
    setConfirmOULoading(true)
    try {
      const data = await getConfirmOUStatus(period)
      setConfirmOUData({
        sustainability: Object.fromEntries(OU_LIST.map(ou => [ou, data.sustainability[ou] ?? false])),
        other_learnings: Object.fromEntries(OU_LIST.map(ou => [ou, data.other_learnings[ou] ?? false])),
      })
    } catch {
      setConfirmOUData({ sustainability: emptyOUChecklist(), other_learnings: emptyOUChecklist() })
    } finally {
      setConfirmOULoading(false)
    }
  }

  const handleConfirmOUOpen = () => {
    setConfirmOUData({ sustainability: {}, other_learnings: {} })
    setConfirmOUTab(0)
    setConfirmOUOpen(true)
    loadConfirmOUData(confirmOUPeriod)
  }

  const handleConfirmOUTabChange = (_: React.SyntheticEvent, newTab: number) => {
    setConfirmOUTab(newTab)
  }

  const handleConfirmOUPeriodChange = (period: string) => {
    setConfirmOUPeriod(period)
    loadConfirmOUData(period)
  }

  const handleConfirmOUSubmit = async () => {
    try {
      await postConfirmOUStatus(confirmOUPeriod, confirmOUData.sustainability, confirmOUData.other_learnings)
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Confirm OU Status saved successfully.', severity: 'success' },
      })
      setConfirmOUOpen(false)
    } catch {
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Failed to save Confirm OU Status. Try again later.', severity: 'warning' },
      })
    }
  }

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const activeFiltersCount = Object.values(appliedFilters).filter(v => v !== undefined && v !== '').length

  const handleSort = (column: ViewEditSortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const resolvedMtoOptions = useMemo(() => {
    if (mtoInitiativeOptions.length > 0) return mtoInitiativeOptions
    return [...new Set(items.map(r => r.mto_initiative).filter(Boolean) as string[])].sort()
  }, [mtoInitiativeOptions, items])

  const sortedItems = useMemo(() => {
    const rows = [...items]
    const dir = sortDirection === 'asc' ? 1 : -1
    rows.sort((a, b) => {
      const aVal = (a[sortColumn] ?? '') as string
      const bVal = (b[sortColumn] ?? '') as string
      return dir * aVal.localeCompare(bVal)
    })
    return rows
  }, [items, sortColumn, sortDirection])

  const renderActionCell = (row: ViewEditAdoptionStatusRow) => {
    const s = (row.status ?? '').toLowerCase()
    const editDisabled = s.includes('activated') || s.includes('ineligible')
    return (
      <span className={styles.viewEditActionCell}>
        <Tooltip
          title={editDisabled ? 'Editing is not allowed for Activated or Ineligible records' : ''}
          disableHoverListener={!editDisabled}>
          <span>
            <IconButton
              size="small"
              onClick={() => handleOpenEdit(row)}
              aria-label="Edit record"
              disabled={editDisabled}>
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {row.has_blocker_comments && (
          <Tooltip title="This record has blocker comments">
            <span
              style={{ color: 'rgba(244,0,9,1)', fontWeight: 700, fontSize: '1rem', lineHeight: 1, cursor: 'default' }}>
              !
            </span>
          </Tooltip>
        )}
      </span>
    )
  }

  const SortableHeader = ({ column, label }: { column: ViewEditSortColumn; label: string }) => (
    <TableSortLabel
      active={sortColumn === column}
      direction={sortColumn === column ? sortDirection : 'asc'}
      onClick={() => handleSort(column)}
      IconComponent={ArrowDropDown}
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        '& .MuiTableSortLabel-icon': {
          color: 'rgba(30, 30, 30, 1) !important',
          fontSize: '30px',
          margin: 0,
        },
      }}>
      {label}
    </TableSortLabel>
  )

  const renderTable = () => (
    <div className={styles.matrixScrollWrapper}>
      <table className={styles.matrixTable}>
        <colgroup>
          <col className={styles.colOU} />
          <col className={styles.colMarket} />
          <col className={styles.colGLAStrategicPriority} />
          <col className={styles.colBPDName} />
          <col className={styles.colLaunched} />
          <col className={styles.colStatus} />
          <col className={styles.colMonth} />
          <col className={styles.colEligibleMkt} />
          <col className={styles.colAction} />
        </colgroup>
        <thead>
          <tr>
            <th className={styles.sortableHeader}>
              <SortableHeader
                column="ou"
                label="OU"
              />
            </th>
            <th className={styles.sortableHeader}>
              <SortableHeader
                column="market"
                label="Market"
              />
            </th>
            <th className={styles.sortableHeader}>
              <SortableHeader
                column="mto_initiative"
                label="GLA Strategic Priority"
              />
            </th>
            <th className={styles.sortableHeader}>
              <SortableHeader
                column="bpd_name"
                label="Published BPDs Scaled"
              />
            </th>
            <th className={styles.sortableHeader}>
              <SortableHeader
                column="bpd_launched_in"
                label="Launched in"
              />
            </th>
            <th className={styles.sortableHeader}>
              <SortableHeader
                column="status"
                label="Status"
              />
            </th>
            <th className={styles.sortableHeader}>
              <SortableHeader
                column="indicate_tentative_month"
                label="Indicate Tentative Month"
              />
            </th>
            <th className={styles.sortableHeader}>
              <SortableHeader
                column="eligible_mkt"
                label="Eligible Mkt"
              />
            </th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map(row => (
            <tr
              key={row.id}
              className={getRowClass(row.status ?? undefined)}>
              <td>{row.ou ?? '-'}</td>
              <td>{row.market ?? '-'}</td>
              <td>{row.mto_initiative ?? '-'}</td>
              <td>{row.bpd_name ?? '-'}</td>
              <td>{row.bpd_launched_in ?? '-'}</td>
              <td>{row.status ?? '-'}</td>
              <td>{row.indicate_tentative_month ?? '-'}</td>
              <td>
                <Checkbox
                  checked={row.eligible_mkt === 'Y'}
                  disabled
                  size="small"
                  sx={{ padding: 0 }}
                />
              </td>
              <td>{renderActionCell(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  const renderEmptyOrError = () => (
    <div className={styles.noExperimentsContainer}>
      {fetchError ? (
        <img
          src={FetchError}
          alt="Fetch error"
        />
      ) : (
        <img
          src={EmptyBox}
          alt="Empty"
        />
      )}
      <p className={styles.noExperimentsHeader}>{fetchError ? 'Error Fetching the Requests' : 'No requests found'}</p>
      {fetchError ? (
        <p className={styles.noExperimentsText}>
          There was an issue retrieving the requests. Check your connection and try again. If the error persists,
          contact the support team.
        </p>
      ) : (
        <p className={styles.noExperimentsText}>Try adjusting your search or filter criteria</p>
      )}
    </div>
  )

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------

  return (
    <div className={styles.summaryByOUContainer}>
      {/* Toolbar */}
      <div className={styles.summaryByOUFilters}>
        {/* Year dropdown */}
        <div className={styles.summaryByOUFilterItem}>
          <FormControl
            size="small"
            fullWidth>
            <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>Year</FormLabel>
            <Select
              value={selectedYear}
              onChange={e => handleYearChange(Number(e.target.value))}>
              {YEAR_OPTIONS.map(y => (
                <MenuItem
                  key={y}
                  value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Quarter dropdown */}
        <div className={styles.summaryByOUFilterItem}>
          <FormControl
            size="small"
            fullWidth>
            <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>Quarter</FormLabel>
            <Select
              value={selectedQuarter}
              onChange={e => handleQuarterChange(e.target.value)}>
              {QUARTER_OPTIONS.map(q => (
                <MenuItem
                  key={q}
                  value={q}>
                  {q}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Filters button — right after YTD */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterListIcon />}
          onClick={handleOpenFilters}
          sx={{ textTransform: 'none', borderColor: 'rgba(0,0,0,0.23)', color: 'rgba(0,0,0,0.6)', height: 40 }}>
          Filters {activeFiltersCount > 0 ? `(${activeFiltersCount})` : ''}
        </Button>

        {/* Freeze Data — visible when viewing YTD, enabled only for admins */}
        {selectedQuarter === 'YTD' && (
          <Button
            variant="outlined"
            size="small"
            disabled={freezing || appStateContext?.state.user?.role !== UserRole.ADMIN}
            onClick={() => setFreezeConfirmOpen(true)}
            sx={{
              textTransform: 'none',
              height: 40,
              borderColor: 'rgba(244,0,9,1)',
              color: 'rgba(244,0,9,1)',
              fontWeight: 700,
              '&:hover': { borderColor: 'rgba(200,0,0,1)', backgroundColor: 'rgba(244,0,9,0.04)' },
            }}>
            {freezing ? (
              <CircularProgress
                size={18}
                sx={{ color: 'rgba(244,0,9,1)' }}
              />
            ) : (
              'Freeze Data'
            )}
          </Button>
        )}

        {/* Confirm OU Status — text link, no box */}
        <Button
          variant="text"
          size="small"
          onClick={handleConfirmOUOpen}
          sx={{
            textTransform: 'none',
            color: 'rgba(244,0,9,1)',
            fontWeight: 700,
            padding: 0,
            minWidth: 'auto',
            height: 40,
          }}>
          Confirm OU Status
        </Button>

        {/* Right side — Non Activated Markets + Refresh */}
        <div className={styles.summaryByOUActions}>
          <FormControlLabel
            control={
              <Switch
                checked={nonActivatedMarketsToggle}
                onChange={e => setNonActivatedMarketsToggle(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: 'rgba(244,0,9,1)' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(244,0,9,1)' },
                }}
              />
            }
            label={<Typography variant="body2">Non Activated Markets</Typography>}
          />

          <IconButton
            size="small"
            onClick={handleResetFilters}
            title="Reset filters">
            <SettingsBackupRestoreIcon />
          </IconButton>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length > 0 ? (
        renderTable()
      ) : (
        renderEmptyOrError()
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Freeze Data confirmation modal                                        */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={freezeConfirmOpen}
        onClose={() => setFreezeConfirmOpen(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, pt: 4, pb: 1 }}>Freeze Data</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary">
            Are you sure you want to freeze the data for {selectedYear}? You won't be able to undo this.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setFreezeConfirmOpen(false)}
            sx={{
              textTransform: 'none',
              borderColor: 'rgba(244,0,9,1)',
              color: 'rgba(244,0,9,1)',
              fontWeight: 600,
              py: 1.2,
            }}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleFreezeData}
            sx={{
              textTransform: 'none',
              backgroundColor: 'rgba(244,0,9,1)',
              fontWeight: 600,
              py: 1.2,
              '&:hover': { backgroundColor: 'rgba(200,0,0,1)' },
            }}>
            Freeze
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Filters modal                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, pb: 1 }}>
          Filters
          <Button
            variant="text"
            size="small"
            onClick={handleClearFilters}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              textTransform: 'none',
              color: 'rgba(244,0,9,1)',
              fontWeight: 600,
            }}>
            Clear all
          </Button>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {/* Non Activated Markets — pill toggle */}
          <Box>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>Non Activated Markets</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {(['No', 'Yes'] as const).map(opt => {
                const active = opt === 'Yes' ? nonActivatedMarketsToggle : !nonActivatedMarketsToggle
                return (
                  <Box
                    key={opt}
                    onClick={() => setNonActivatedMarketsToggle(opt === 'Yes')}
                    sx={{
                      px: 3,
                      py: 1,
                      borderRadius: '999px',
                      border: '1.5px solid',
                      borderColor: active ? 'rgba(244,0,9,0.3)' : 'rgba(0,0,0,0.23)',
                      backgroundColor: active ? 'rgba(244,0,9,0.08)' : 'transparent',
                      color: active ? 'rgba(244,0,9,1)' : 'text.primary',
                      fontWeight: active ? 700 : 400,
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontSize: 14,
                    }}>
                    {opt}
                  </Box>
                )
              })}
            </Box>
          </Box>

          {/* OU + GLA Strategic Priority */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormControl
              size="small"
              fullWidth>
              <FormLabel sx={{ fontSize: 13, mb: 0.5 }}>OU</FormLabel>
              <Select
                value={pendingFilters.ou ?? ''}
                displayEmpty
                onChange={e => setPendingFilters(prev => ({ ...prev, ou: e.target.value || undefined }))}>
                <MenuItem value="">
                  <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select</em>
                </MenuItem>
                {ouOptions.map(v => (
                  <MenuItem
                    key={v}
                    value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              fullWidth>
              <FormLabel sx={{ fontSize: 13, mb: 0.5 }}>GLA Strategic Priority</FormLabel>
              <Select
                value={pendingFilters.mto_initiative ?? ''}
                displayEmpty
                onChange={e => setPendingFilters(prev => ({ ...prev, mto_initiative: e.target.value || undefined }))}>
                <MenuItem value="">
                  <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select</em>
                </MenuItem>
                {resolvedMtoOptions.map(v => (
                  <MenuItem
                    key={v}
                    value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* BPD + Status */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <FormControl
              size="small"
              fullWidth>
              <FormLabel sx={{ fontSize: 13, mb: 0.5 }}>BPD</FormLabel>
              <Select
                value={pendingFilters.bpd ?? ''}
                displayEmpty
                onChange={e => setPendingFilters(prev => ({ ...prev, bpd: e.target.value || undefined }))}>
                <MenuItem value="">
                  <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select</em>
                </MenuItem>
                {bpdOptions.map(v => (
                  <MenuItem
                    key={v}
                    value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              fullWidth>
              <FormLabel sx={{ fontSize: 13, mb: 0.5 }}>Status</FormLabel>
              <Select
                value={pendingFilters.status ?? ''}
                displayEmpty
                onChange={e => setPendingFilters(prev => ({ ...prev, status: e.target.value || undefined }))}>
                <MenuItem value="">
                  <em style={{ color: '#aaa', fontStyle: 'normal' }}>Select</em>
                </MenuItem>
                {BPD_ACTIVATION_STATUS_OPTIONS.map(s => (
                  <MenuItem
                    key={s}
                    value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setFiltersOpen(false)}
            sx={{
              textTransform: 'none',
              borderColor: 'rgba(244,0,9,1)',
              color: 'rgba(244,0,9,1)',
              fontWeight: 600,
              py: 1.5,
            }}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleApplyFilters}
            sx={{
              textTransform: 'none',
              backgroundColor: 'rgba(244,0,9,1)',
              fontWeight: 600,
              py: 1.5,
              '&:hover': { backgroundColor: 'rgba(200,0,0,1)' },
            }}>
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Edit modal                                                           */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={editOpen}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography
            fontWeight={700}
            fontSize={20}>
            Update Adoption Status
          </Typography>
          <IconButton
            size="small"
            onClick={handleCloseEdit}
            aria-label="Close">
            ✕
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          {editLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Eligible Market — checkbox at top */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontWeight={700}>Eligible Market</Typography>
                <Checkbox
                  checked={editRecord?.eligible_mkt === 'Y'}
                  disabled
                  size="small"
                  sx={{ padding: 0 }}
                />
              </Box>

              {/* Read-only fields — 2-column grid with outlined floating labels */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  label="OU"
                  value={editRecord?.ou ?? ''}
                  size="small"
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Market"
                  value={editRecord?.market ?? ''}
                  size="small"
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Learning Name"
                  value={editRecord?.bpd_name ?? ''}
                  size="small"
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="GLA Strategic Priority"
                  value={editRecord?.mto_initiative ?? ''}
                  size="small"
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              {/* BPD Activation Status (editable) + Indicate Tentative Month (editable) */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  select
                  label="BPD Activation Status"
                  value={editPayload.status ?? ''}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  onChange={e => setEditPayload(prev => ({ ...prev, status: e.target.value }))}>
                  <MenuItem value="">
                    <em style={{ fontStyle: 'normal', color: '#aaa' }}>—</em>
                  </MenuItem>
                  {getEditStatusOptions(selectedYear).map(s => (
                    <MenuItem
                      key={s}
                      value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Indicate Tentative Month"
                  value={editPayload.indicate_tentative_month ?? ''}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  onChange={e => setEditPayload(prev => ({ ...prev, indicate_tentative_month: e.target.value }))}>
                  <MenuItem value="">
                    <em style={{ fontStyle: 'normal', color: '#aaa' }}>—</em>
                  </MenuItem>
                  {MONTH_OPTIONS.map(m => (
                    <MenuItem
                      key={m}
                      value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Brands + Charters */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  select
                  label="Brands"
                  value={editPayload.brands ?? []}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: selected => {
                      const arr = selected as string[]
                      return arr.length > 0 ? (
                        arr.join(', ')
                      ) : (
                        <em style={{ fontStyle: 'normal', color: '#aaa' }}>Find Items</em>
                      )
                    },
                  }}
                  onChange={e =>
                    setEditPayload(prev => ({
                      ...prev,
                      brands: e.target.value as unknown as string[],
                    }))
                  }>
                  {BRAND_OPTIONS.map(b => (
                    <MenuItem
                      key={b}
                      value={b}>
                      <Checkbox
                        checked={(editPayload.brands ?? []).includes(b)}
                        size="small"
                        sx={{ py: 0, pl: 0 }}
                      />
                      {b}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Charters"
                  value={editPayload.charters ?? []}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: selected => {
                      const arr = selected as string[]
                      return arr.length > 0 ? (
                        arr.join(', ')
                      ) : (
                        <em style={{ fontStyle: 'normal', color: '#aaa' }}>Find Items</em>
                      )
                    },
                  }}
                  onChange={e =>
                    setEditPayload(prev => ({
                      ...prev,
                      charters: e.target.value as unknown as string[],
                    }))
                  }>
                  {CHARTER_OPTIONS.map(c => (
                    <MenuItem
                      key={c}
                      value={c}>
                      <Checkbox
                        checked={(editPayload.charters ?? []).includes(c)}
                        size="small"
                        sx={{ py: 0, pl: 0 }}
                      />
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Blocker comment — full width with floating label */}
              <TextField
                multiline
                minRows={3}
                label="Please, input Blockers for BPD adoption or any support you need"
                value={editPayload.blocker_comment ?? ''}
                InputLabelProps={{ shrink: true }}
                onChange={e => setEditPayload(prev => ({ ...prev, blocker_comment: e.target.value }))}
              />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleCloseEdit}
            sx={{
              textTransform: 'none',
              borderColor: 'rgba(244,0,9,1)',
              color: 'rgba(244,0,9,1)',
              fontWeight: 600,
              py: 1.5,
            }}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmitEdit}
            disabled={editLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              backgroundColor: 'rgba(100,100,100,1)',
              '&:hover': { backgroundColor: 'rgba(60,60,60,1)' },
              '&:not(:disabled)': {
                backgroundColor: 'rgba(244,0,9,1)',
                '&:hover': { backgroundColor: 'rgba(200,0,0,1)' },
              },
            }}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Confirm OU Status modal                                              */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={confirmOUOpen}
        onClose={() => setConfirmOUOpen(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography
                fontWeight={700}
                fontSize={20}>
                Update Adoption Status
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary">
                Update confirmation status for {confirmOUPeriod}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setConfirmOUOpen(false)}
              aria-label="Close"
              sx={{ mt: 0.5 }}>
              ✕
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {/* Period selection */}
          <FormControl
            size="small"
            fullWidth>
            <FormLabel sx={{ fontSize: 12, mb: 0.5, color: 'text.secondary' }}>Selected Period</FormLabel>
            <Select
              value={confirmOUPeriod}
              onChange={e => handleConfirmOUPeriodChange(e.target.value)}>
              {[CURRENT_YEAR - 1, CURRENT_YEAR].flatMap(y =>
                ['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
                  <MenuItem
                    key={`${q} ${y}`}
                    value={`${q} ${y}`}>
                    {q} {y}
                  </MenuItem>
                )),
              )}
            </Select>
          </FormControl>

          {/* Tabs: Sustainability / Other Learnings */}
          <Tabs
            value={confirmOUTab}
            onChange={handleConfirmOUTabChange}
            sx={{
              borderBottom: '1px solid rgba(0,0,0,0.12)',
              '& .MuiTab-root': { textTransform: 'uppercase', fontWeight: 700, fontSize: 13 },
              '& .Mui-selected': { color: 'rgba(244,0,9,1)' },
              '& .MuiTabs-indicator': { backgroundColor: 'rgba(244,0,9,1)' },
            }}>
            <Tab label="Sustainability" />
            <Tab label="Other Learnings" />
          </Tabs>

          {/* Responsible badge */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              border: '1px solid rgba(0,0,0,0.23)',
              borderRadius: '999px',
              px: 1.5,
              py: 0.5,
              fontSize: 13,
              color: 'text.primary',
              alignSelf: 'flex-start',
            }}>
            {confirmOUTab === 0 ? 'Responsible: TCCC OU Orchestrators' : 'Responsible: WPP OU Leads'}
          </Box>

          {/* OU list with toggles */}
          <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 300, overflowY: 'auto' }}>
            {confirmOULoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              OU_LIST.map(ou => {
                const tabKey = confirmOUTab === 0 ? 'sustainability' : 'other_learnings'
                return (
                  <Box
                    key={ou}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.5,
                      py: 1,
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: 1,
                      mb: 0.5,
                    }}>
                    <Typography fontSize={14}>{ou}</Typography>
                    <Switch
                      checked={confirmOUData[tabKey]?.[ou] ?? false}
                      onChange={e =>
                        setConfirmOUData(prev => ({
                          ...prev,
                          [tabKey]: { ...prev[tabKey], [ou]: e.target.checked },
                        }))
                      }
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: 'rgba(244,0,9,1)' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'rgba(244,0,9,1)',
                        },
                      }}
                    />
                  </Box>
                )
              })
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setConfirmOUOpen(false)}
            sx={{
              textTransform: 'none',
              borderColor: 'rgba(244,0,9,1)',
              color: 'rgba(244,0,9,1)',
              fontWeight: 600,
              py: 1.2,
            }}>
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirmOUSubmit}
            disabled={confirmOULoading}
            sx={{
              textTransform: 'none',
              backgroundColor: 'rgba(244,0,9,1)',
              fontWeight: 600,
              py: 1.2,
              '&:hover': { backgroundColor: 'rgba(200,0,0,1)' },
            }}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helper sub-component
// ---------------------------------------------------------------------------

const ReadOnlyField = ({ label, value }: { label: string; value?: string | null }) => (
  <Box>
    <FormLabel sx={{ fontSize: 12 }}>{label}</FormLabel>
    <Typography
      variant="body2"
      sx={{ mt: 0.5 }}>
      {value ?? '-'}
    </Typography>
  </Box>
)

export default ViewEditAdoptionStatusTab
